import { CurecallDateTimePicker, CurecallDateTimePickerHandle } from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { useCallback, useEffect, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import { EConvocationDateSetBy, TConvocationConfig, TMedEventProperty } from 'src/types/med-event'
import { TNavigationLocales } from 'src/types/locales'
import { getTranslatedLabel } from 'src/utils/locale.util'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import duration from 'dayjs/plugin/duration'

dayjs.extend(customParseFormat)
dayjs.extend(duration)

type TConvocationDatePickerProps = {
  property: TMedEventProperty
  convocationConfig?: TConvocationConfig
  timezone?: string
}

const ConvocationDatePicker = ({ property, convocationConfig, timezone }: TConvocationDatePickerProps) => {
  const {
    setValue,
    clearErrors,
    register,
    formState: { errors, defaultValues },
    getValues,
    watch,
  } = useFormContext()
  const { t } = useTranslation()
  const { i18n } = useTranslation()
  const locale = i18n.language as TNavigationLocales

  const patientMedEventDate = watch('date')
  const convocationDateAlreadySaved = getValues(property.fieldName)
  const defaultSelectedDateAndTime = convocationDateAlreadySaved || defaultValues?.[property.fieldName]
  const convocationDateTimePickerRef = useRef<CurecallDateTimePickerHandle>(null)

  const handleOnChangeDateTime = useCallback(
    (date: string | null) => {
      setValue(property.fieldName, date)
      setValue('convocationDateSetBy', EConvocationDateSetBy.USER)
      if (date) clearErrors(property.fieldName)
    },
    [property.fieldName, setValue, clearErrors],
  )

  useEffect(() => {
    register(property.fieldName, { required: property.isRequired })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [register])

  useEffect(() => {
    const hasUpdatedPatientMedEventDate = !dayjs(patientMedEventDate).isSame(defaultValues?.['date'])
    if (!hasUpdatedPatientMedEventDate || !convocationConfig?.convocationOffset || !patientMedEventDate) {
      return
    }

    const offsetDuration = dayjs.duration(convocationConfig.convocationOffset)
    let calculatedConvocationDate = dayjs(patientMedEventDate).subtract(offsetDuration)

    if (convocationConfig.serviceOpeningTime) {
      const openingTime = dayjs(convocationConfig.serviceOpeningTime, 'HH:mm')
      const serviceOpensAt = calculatedConvocationDate
        .set('hour', openingTime.hour())
        .set('minute', openingTime.minute())
      if (calculatedConvocationDate.isBefore(serviceOpensAt)) {
        calculatedConvocationDate = serviceOpensAt
      }
    }

    if (convocationConfig.serviceClosingTime) {
      const closingTime = dayjs(convocationConfig.serviceClosingTime, 'HH:mm')
      const serviceClosesAt = calculatedConvocationDate
        .set('hour', closingTime.hour())
        .set('minute', closingTime.minute())
      if (calculatedConvocationDate.isAfter(serviceClosesAt)) {
        calculatedConvocationDate = serviceClosesAt
      }
    }

    const updatedConvocationDateTime = calculatedConvocationDate.format()
    convocationDateTimePickerRef.current?.setDateTime(updatedConvocationDateTime)
    setValue(property.fieldName, updatedConvocationDateTime)
    setValue('convocationDateSetBy', EConvocationDateSetBy.CONFIG)
    clearErrors(property.fieldName)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convocationConfig, patientMedEventDate])

  return (
    <CurecallDateTimePicker
      ref={convocationDateTimePickerRef}
      label={getTranslatedLabel(locale, property.label)}
      onChangeDateTime={handleOnChangeDateTime}
      defaultDateTime={defaultSelectedDateAndTime}
      isRequired={property.isRequired}
      localeLanguage={navigator.language}
      errorMessage={
        errors[property.fieldName] ? t('components.generic-followups-form.please-enter-date-and-hour') : ''
      }
      tooltipContent={t('components.generic-followups-form.convocation-tooltip')}
      tooltipPosition="bottom"
      inputClassName="c-w-[170px]"
      timezone={timezone}
    />
  )
}

export default ConvocationDatePicker
